<template>
	<div class="map-view">
		<div ref="map" class="map"></div>
		<xyz-transition-group appear xyz="fade small appear-stagger" tag="div" class="map-buttons">
			<button
				type="button"
				class="center-pins button--help button--translucent translucent--white"
				v-if="locations.length"
				@click="fitLocations(true)"
				key="button-pins"
			>
				<icon-place></icon-place>
			</button>
			<button
				type="button"
				class="find-my-location button--help button--translucent translucent--white"
				:class="{ pulse: findingUserLocation }"
				@click="goToUserLocation(true)"
				key="button-location"
			>
				<icon-location></icon-location>
			</button>
		</xyz-transition-group>
		<map-marker
			class="c-pointer"
			:class="locationClassObj(location)"
			:shape="location.shape"
			:map="map"
			:position="location.position"
			v-for="(location, locationIndex) in sortedLocations"
			@marker-click="onLocationClick($event, location)"
			:key="locationIndex"
		>
			<slot name="marker" :location="location"></slot>
		</map-marker>
		<popover
			class="popover"
			name="mapPopover"
			:ref-elem="clickedLocation.elem"
			v-if="$scopedSlots.popover && clickedLocation"
			:show="true"
			ref="popover"
		>
			<slot name="popover" :selected="clickedLocation.selected"></slot>
		</popover>
	</div>
</template>

<script>
import localConfig from '@/localConfig'
import IconLocation from '@/components/icons/IconLocation.svg'
import IconPlace from '@/components/icons/IconPlace.svg'
import MapMarker from '@/components/reusable/MapMarker'
import Popover from '@/components/reusable/Popover'
import mapboxgl from 'mapbox-gl'
import { mapState, mapActions } from 'vuex'

mapboxgl.accessToken = localConfig.mapbox.key
let mapInstance = null // Global map instance to avoid unnecessary map loads

export default {
	name: 'MapView',
	props: {
		locations: {
			type: Array,
			required: true,
		},
	},
	components: {
		IconLocation,
		IconPlace,
		MapMarker,
		Popover,
	},
	data() {
		return {
			map: null,
			initialZoom: 11,
			pointZoom: 14,
			maxZoom: 17,
			flyDuration: 1000,
			zoomPadding: 60,
			zoomLockTimeout: null,
			zoomLockDebounce: 200,
			findingUserLocation: false,
			clickedLocation: null,
		}
	},
	computed: {
		...mapState({
			userLocation: (state) => state.userLocation,
		}),
		sortedLocations() {
			const sortedLocations = [...this.locations]
			sortedLocations.sort((a, b) => {
				return b.position.lat - a.position.lat
			})
			return sortedLocations
		},
	},
	watch: {
		locations() {
			this.fitLocations(true)
		},
	},
	methods: {
		...mapActions(['getUserIpLocation', 'getUserLocation']),
		async initMap() {
			if (!mapInstance) {
				mapInstance = new mapboxgl.Map({
					container: this.$refs.map,
					style: 'mapbox://styles/mapbox/light-v10',
					maxZoom: this.maxZoom,
					zoom: this.initialZoom,
					pitchWithRotate: false,
					dragRotate: false,
					attributionControl: false,
				})
				mapInstance.addControl(new mapboxgl.AttributionControl(), 'top-left')
			} else {
				const oldContainer = mapInstance.getContainer()
				const newContainer = this.$refs.map
				newContainer.classList.add('mapboxgl-map')
				while (oldContainer.childNodes.length > 0) {
					newContainer.appendChild(oldContainer.childNodes[0])
				}
				mapInstance._container = newContainer
			}
			mapInstance.resize()
			this.map = mapInstance

			this.map.on('move', this.onMove)

			if (!this.userLocation) {
				await this.getUserIpLocation()
			}
			if (this.locations.length) {
				this.fitLocations(false)
			} else {
				this.map.setCenter(this.userLocation)
			}
		},
		fitLocations(animate) {
			if (this.locations.length === 1) {
				this.map.flyTo({
					center: this.locations[0].position,
					zoom: this.pointZoom,
					duration: animate ? this.flyDuration : 0,
				})
			} else if (this.locations.length > 1) {
				const bounds = new mapboxgl.LngLatBounds()

				this.locations.forEach((location) => {
					bounds.extend(new mapboxgl.LngLat(location.position.lng, location.position.lat))
				})

				this.map.fitBounds(bounds, {
					padding: this.zoomPadding,
					duration: animate ? this.flyDuration : 0,
				})
			}
		},
		locationClassObj(location) {
			const classObj = {}
			if (location.color) {
				classObj[`current-color--${location.color}`] = true
			}
			return classObj
		},
		async goToUserLocation(animate) {
			this.findingUserLocation = true
			await this.getUserLocation()
			this.map.flyTo({
				center: this.userLocation,
				zoom: this.initialZoom,
				duration: animate ? this.flyDuration : 0,
			})
			this.findingUserLocation = false
		},
		inBounds(bounds, point) {
			const lng = (point.lng - bounds.getEast()) * (point.lng - bounds.getWest()) < 0
			const lat = (point.lat - bounds.getNorth()) * (point.lat - bounds.getSouth()) < 0
			return lng && lat
		},
		onLocationClick(event, location) {
			this.clickedLocation = {
				elem: event.currentTarget,
				selected: [location],
			}
			this.$emit('location-click', location)
			event.stopPropagation()
		},
		onMove(event) {
			if (this.$refs.popover) {
				this.$refs.popover.updatePosition()
			}
			this.$emit('move', event)
		},
		onWindowScroll() {
			this.map.scrollZoom.disable()
			clearTimeout(this.zoomLockTimeout)
			this.zoomLockTimeout = setTimeout(() => {
				this.map.scrollZoom.enable()
			}, this.zoomLockDebounce)
		},
		onDocumentClick() {
			this.clickedLocation = null
		},
	},
	mounted() {
		this.initMap()
		window.addEventListener('scroll', this.onWindowScroll)
		document.addEventListener('click', this.onDocumentClick)
	},
	beforeDestroy() {
		window.removeEventListener('scroll', this.onWindowScroll)
		document.removeEventListener('click', this.onDocumentClick)
		if (this.zoomLockTimeout) {
			clearTimeout(this.zoomLockTimeout)
		}
	},
}
</script>

<style lang="scss" scoped>
.map-view {
	position: relative;
	width: 100%;
	height: 280px;
}

.map {
	width: 100%;
	height: 100%;
}

.map-buttons {
	position: absolute;
	bottom: $spacing-xxs;
	right: $spacing-xxs;
	z-index: $z-forward-1;
	display: flex;
	flex-direction: column;

	button {
		margin-top: $spacing-xxs;
	}
}

.find-my-location {
	.icon-location {
		position: relative;
		top: 1px;
		right: 1px;
	}

	&.pulse {
		&::before {
			opacity: 0.75;
			animation: shadowPulse 1.25s $ease-out-quad infinite;
		}
	}
}
</style>
